import type { LayerDefinition, LayerLegend, LegendItem } from "@/types/explore"
import { PRESC_INFO_IDS } from "@/lib/explore/constants"

const PANEL_BASE =
  "bg-white/[0.97] backdrop-blur-md map-panel-shadow rounded-lg px-3 py-2.5 anim-fade-in border border-white/60"
const TITLE_CLS = "text-xs font-semibold text-gray-700 tracking-wide mb-1.5 leading-none"

interface LayerLegendsProps {
  activeLayerItems: LayerDefinition[]
}

const LegendShape = ({ item, size = 12 }: { item: LegendItem; size?: number }) => {
  const shape = item.shape || "square"
  if (shape === "circle") {
    return (
      <span
        className="inline-block rounded-full shrink-0 ring-1 ring-black/10"
        style={{ width: size, height: size, backgroundColor: item.color }}
      />
    )
  }
  if (shape === "line") {
    return (
      <span
        className="inline-block shrink-0 rounded-full"
        style={{
          width: size + 6,
          height: 3,
          backgroundColor: item.color,
          boxShadow: `0 0 4px ${item.color}40`,
        }}
      />
    )
  }
  return (
    <span
      className="inline-block rounded-sm shrink-0 ring-1 ring-black/10"
      style={{ width: size, height: size, backgroundColor: item.color }}
    />
  )
}

const GradientLegend = ({ legend }: { legend: LayerLegend }) => {
  const gradient = `linear-gradient(to right, ${legend.items.map((c) => c.color).join(", ")})`
  return (
    <div className={PANEL_BASE} style={{ minWidth: 140 }}>
      <div className={TITLE_CLS}>{legend.label}</div>
      <div className="legend-bar mb-1.5" style={{ background: gradient }} />
      <div className="flex justify-between">
        {legend.items.map((c, i) => (
          <span
            key={i}
            className="text-xs text-gray-500 flex flex-col items-center"
            style={{
              textAlign: i === 0 ? "left" : i === legend.items.length - 1 ? "right" : "center",
              flex: 1,
            }}
          >
            <span className="rounded-full mb-0.5" style={{ width: 5, height: 5, backgroundColor: c.color }} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

const ItemsLegend = ({ legend }: { legend: LayerLegend }) => (
  <div className={PANEL_BASE} style={{ minWidth: 100 }}>
    <div className={TITLE_CLS}>{legend.label}</div>
    <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto scrollbar-thin">
      {legend.items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <LegendShape item={item} size={11} />
          <span className="text-xs text-gray-600">{item.label}</span>
        </div>
      ))}
    </div>
  </div>
)

const SwatchPill = ({ legend }: { legend: LayerLegend }) => {
  const item = legend.items[0]
  return (
    <div className={`${PANEL_BASE} !px-0 !py-0 overflow-hidden flex items-center`}>
      <div className="shrink-0 self-stretch" style={{ width: 4, backgroundColor: item.color }} />
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <LegendShape item={item} size={10} />
        <span className="text-xs text-gray-600 whitespace-nowrap">{legend.label}</span>
      </div>
    </div>
  )
}

const GroupedSwatchPanel = ({ layers }: { layers: LayerDefinition[] }) => {
  const useGrid = layers.length > 4
  return (
    <div className={PANEL_BASE} style={{ minWidth: useGrid ? 220 : 120, maxWidth: 280 }}>
      <div className={TITLE_CLS}>Urbanisme</div>
      <div
        className={`${useGrid ? "grid grid-cols-2 gap-1.5" : "flex flex-col gap-1.5"} max-h-48 overflow-y-auto scrollbar-thin`}
      >
        {layers.map((layer) => {
          const item = layer.legend!.items[0]
          return (
            <div key={layer.id} className="flex items-center gap-2">
              <LegendShape item={item} size={11} />
              <span className="text-xs text-gray-600 truncate">{layer.legend!.label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const LayerLegends = ({ activeLayerItems }: LayerLegendsProps) => {
  const withLegend = activeLayerItems.filter((l) => l.legend)
  if (withLegend.length === 0) return null

  const prescInfoLayers = withLegend.filter((l) => PRESC_INFO_IDS.has(l.id))
  const otherLayers = withLegend.filter((l) => !PRESC_INFO_IDS.has(l.id))

  return (
    <div className="absolute bottom-3 right-[300px] z-10 flex gap-2.5 flex-wrap-reverse items-end">
      {prescInfoLayers.length > 0 && <GroupedSwatchPanel layers={prescInfoLayers} />}
      {otherLayers.map((layer) => {
        const legend = layer.legend!
        if (legend.type === "gradient") return <GradientLegend key={layer.id} legend={legend} />
        if (legend.type === "items") return <ItemsLegend key={layer.id} legend={legend} />
        return <SwatchPill key={layer.id} legend={legend} />
      })}
    </div>
  )
}
